import { ElementRef, inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { AbstractService } from './abstract-service';

export interface ShortcutServiceState {
  elements: SolShortcutElement[];
}

export interface SolShortcutElement {
  id: number;
  name: string;
  modal: boolean;
  zIndex: number;
  actions: SolShortcutAction[];
}

export interface SolShortcutAction {
  shortcut: SolShortcut;
  action: () => boolean;
}

export class SolShortcut {
  private constructor(public readonly code: string) {}

  /**
   * Creates and returns a new instance of SolShortcut initialized with the provided code.
   *
   * Examples:
   * - Escape
   * - Ctrl+S
   * - Ctrl+Enter
   * - Alt+F5
   * - Ctrl+Alt+Shift+Delete
   */
  public static of(code: string): SolShortcut {
    return new SolShortcut(code);
  }

  public static ofKeyboardEvent(event: KeyboardEvent): SolShortcut {
    const key = event.key;
    const code: string = [
      ...(event.ctrlKey ? ['Ctrl'] : []),
      ...(event.altKey ? ['Alt'] : []),
      ...(event.shiftKey ? ['Shift'] : []),
      key,
    ].join('+');
    return this.of(code);
  }

  public get ctrl(): boolean {
    return this.code.includes('Ctrl+');
  }

  public get alt(): boolean {
    return this.code.includes('Alt+');
  }

  public get shift(): boolean {
    return this.code.includes('Shift+');
  }

  public get key(): string {
    return this.code.split('+').pop() || '';
  }

  public get hasModifiers(): boolean {
    return this.ctrl || this.alt || this.shift;
  }

  public isCommit(): boolean {
    return !this.hasModifiers && this.code === 'Enter';
  }

  /** Escape dismisses the top layer, so it belongs to the layer stack rather than to a focused field. */
  public isDismiss(): boolean {
    return !this.hasModifiers && this.code === 'Escape';
  }

  public matchesCode(other: string): boolean {
    return this.code.toLowerCase() === other.toLowerCase();
  }
}

export interface SolShortcutSubscription {
  unsubscribe: () => void;
}

interface SolShortcutLayer {
  id: string;
  zIndex: number;
}

@Injectable({
  providedIn: 'root',
})
export class ShortcutService extends AbstractService<ShortcutServiceState> implements OnDestroy {
  private static readonly layers: SolShortcutLayer[] = [
    { id: 'app', zIndex: 0 },
    { id: 'content', zIndex: 1000 },
    { id: 'detail', zIndex: 2000 },
    { id: 'modal', zIndex: 3000 },
    { id: 'popover', zIndex: 4000 },
  ];

  private readonly ngZone = inject(NgZone);
  private readonly keydownListener = (event: KeyboardEvent) => this.handleEvent(event);
  private id: number = 0;

  public constructor() {
    super({
      elements: [],
    });

    this.listenOutsideZone('keydown', this.keydownListener);
  }

  public ngOnDestroy(): void {
    this.unlistenOutsideZone('keydown', this.keydownListener);
  }

  public register(
    name: string,
    modal: boolean,
    host: ElementRef<HTMLElement>,
    actions: SolShortcutAction[],
  ): SolShortcutSubscription {
    const id = this.id++;
    const zIndex = ShortcutService.findClosestZIndex(host.nativeElement);
    const layer: SolShortcutElement = {
      id: id,
      name: name,
      modal: modal,
      zIndex: zIndex,
      actions: actions,
    };
    this.update(state => ({
      elements: [...state.elements, layer].sort((a, b) => a.zIndex - b.zIndex || a.id - b.id),
    }));
    return {
      unsubscribe: () => this.unregister(id),
    };
  }

  private unregister(id: number): void {
    this.patch({
      elements: this.state.elements.filter(layer => layer.id !== id),
    });
  }

  /**
   * Checks if keyboard focus is on a regular input, checkbox or other focusable content
   */
  private shouldIgnore(event: KeyboardEvent, shortcut: SolShortcut): boolean {
    // Ignore if the target is not determined.
    const target = event.target as HTMLElement;

    if (!target) {
      return true;
    }

    // Ignore events if the target is an input field, textarea, or content-editable element.
    const tagName = target.tagName.toLowerCase();
    const editable = target.isContentEditable;
    const isMultilineEditable = tagName === 'textarea' || editable;
    const escapesForCommit = shortcut.isCommit() && !isMultilineEditable;
    const noModifiersOrCommit = !shortcut.hasModifiers && !escapesForCommit;
    if (noModifiersOrCommit && (tagName === 'input' || tagName === 'textarea' || editable)) {
      return true;
    }

    return ShortcutService.claimsKey(target, shortcut);
  }

  /**
   * Resolves the nearest `data-shortcut-ignore` declaration from the focused element upwards, so a
   * composite claims keys for the elements it renders rather than only for the element focus landed on.
   *
   * An empty declaration claims every modifier-less key except Escape. Enter is included — stronger
   * than the implicit rule above, which lets Enter through so a container's confirm can fire, because
   * a declaration is an explicit statement that this composite commits its own typing. Escape is not,
   * because it dismisses the top layer and that is what the layer walk exists to route; a component
   * that genuinely handles Escape lists it. A comma-separated declaration claims the listed codes
   * only. Codes are `KeyboardEvent.key` values, so Space is `' '`.
   */
  private static claimsKey(target: HTMLElement, shortcut: SolShortcut): boolean {
    let el: HTMLElement | null = target;

    while (el) {
      const declaration = el.getAttribute('data-shortcut-ignore');
      if (declaration !== null) {
        const codes = declaration.split(',').filter(code => code.length > 0);
        return codes.length === 0
          ? !shortcut.hasModifiers && !shortcut.isDismiss()
          : codes.some(code => shortcut.matchesCode(code));
      }
      el = el.parentElement;
    }

    return false;
  }

  private handleEvent(event: KeyboardEvent): void {
    const shortcut = SolShortcut.ofKeyboardEvent(event);
    const elements = this.state.elements;

    if (this.shouldIgnore(event, shortcut)) {
      return;
    }

    for (let elementIndex = elements.length - 1; elementIndex >= 0; elementIndex--) {
      const element = elements[elementIndex];

      // Check each action shortcut
      for (const action of element.actions) {
        if (action.shortcut.matchesCode(shortcut.code)) {
          const consumed = this.ngZone.run(() => action.action());
          if (consumed) {
            event.preventDefault();
            console.debug(`Shortcut element ${element.name} handled action ${action.action.name}`);
            return;
          }
        }
      }

      // If the element is modal, stop further processing
      if (element.modal) {
        return;
      }
    }
    console.debug(`Keyboard event ${shortcut.code} not handled`);
  }

  /** Registers a window event listener outside zone.js to avoid passive listener consolidation (Angular #45020). */
  private listenOutsideZone(type: string, handler: (event: KeyboardEvent) => void): void {
    const add = (window as any)['__zone_symbol__addEventListener'] ?? window.addEventListener.bind(window);
    add.call(window, type, handler, { passive: false });
  }

  private unlistenOutsideZone(type: string, handler: (event: KeyboardEvent) => void): void {
    const remove = (window as any)['__zone_symbol__removeEventListener'] ?? window.removeEventListener.bind(window);
    remove.call(window, type, handler, { passive: false });
  }

  private static findClosestZIndex(startElement: HTMLElement): number {
    let el: HTMLElement | null = startElement;

    while (el) {
      const value = el.getAttribute('data-shortcut-layer') as string;
      const zIndex = this.findZIndex(value);
      if (zIndex !== undefined) {
        return zIndex;
      }
      el = el.parentElement;
    }

    return this.layers[0].zIndex;
  }

  private static findZIndex(value: string | undefined): number | undefined {
    if (value === undefined) {
      return undefined;
    }
    return this.layers.find(layer => value === layer.id)?.zIndex;
  }
}
